import React, { useState, useEffect, useMemo } from 'react';
import {
  FiSearch,
  FiEdit,
  FiTrash2,
  FiPlus,
  FiSave,
  FiX,
  FiChevronDown,
  FiChevronUp,
  FiTrendingUp,
  FiCalendar,
  FiHome,
  FiAward,
  FiGlobe,
  FiClock,
  FiSliders,
  FiFolder,
  FiArrowRight,
  FiMoreVertical,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;
const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// ============================================
// Helper: Assign Icon & Colors based on category name
// ============================================
const getCategoryDesign = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('demand') || n.includes('yield'))
    return { icon: <FiTrendingUp size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' };
  if (n.includes('season') || n.includes('time'))
    return { icon: <FiCalendar size={20} />, color: 'text-indigo-600', bg: 'bg-indigo-50' };
  if (n.includes('room') || n.includes('occupancy'))
    return { icon: <FiHome size={20} />, color: 'text-blue-500', bg: 'bg-blue-50' };
  if (n.includes('loyal') || n.includes('offer'))
    return { icon: <FiAward size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' };
  if (n.includes('market') || n.includes('competition'))
    return { icon: <FiGlobe size={20} />, color: 'text-blue-600', bg: 'bg-blue-50' };
  return { icon: <FiFolder size={20} />, color: 'text-gray-600', bg: 'bg-gray-50' };
};

const FACTOR_TYPES = [
  { value: 'amenity', label: 'Room Amenity' },
  { value: 'ac', label: 'Air Conditioning' },
  { value: 'bedtype', label: 'Bed Type' },
  { value: 'service', label: 'Guest Service' },
  { value: 'discount', label: 'Promotions & Discount' },
  { value: 'auto', label: 'Auto-Calculated' },
  { value: 'seasonal', label: 'Seasonal' },
  { value: 'event', label: 'Event' },
];

const VALUE_TYPES = [
  { value: 'flat', label: 'Flat Amount (₹)' },
  { value: 'percentage', label: 'Percentage (%)' },
];

const PRICING_ELIGIBLE_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'optional', label: 'Optional' },
];

const Toggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onChange}
    className={`w-11 h-6 rounded-full transition ${checked ? 'bg-black' : 'bg-gray-300'} ${
      disabled ? 'opacity-50 cursor-not-allowed' : ''
    }`}
  >
    <span
      className={`block w-4 h-4 bg-white rounded-full transition-transform mt-1 ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const ModifyFactors = () => {
  const [activeTab, setActiveTab] = useState('categories'); // 'categories' | 'factors'

  // Factor State
  const [stats, setStats] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [factorToDelete, setFactorToDelete] = useState(null);
  const [editedStats, setEditedStats] = useState([]);
  const [isConfirmEditOpen, setIsConfirmEditOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [roomTypesLoading, setRoomTypesLoading] = useState(false);
  const [expandedFactor, setExpandedFactor] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [newFactor, setNewFactor] = useState({
    title: '',
    factor_type: 'service',
    value_type: 'percentage',
    default_value: '',
    min_guests: 0,
    show_in_bq: true,
    is_room_amenity: false,
    auto_apply: false,
    isActive: true,
    description: '',
    category_id: '',
    per_room_type_values: [],
  });

  // Category State
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState('create');
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    pricing_eligible: 'optional',
    is_active: true,
  });
  const [isConfirmCategoryDeleteOpen, setIsConfirmCategoryDeleteOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const navigate = useNavigate();

  // ============================================
  // Fetch Functions
  // ============================================
  const getRoomTypeIdByName = (name) =>
    roomTypes.find((r) => r.roomtypename === name)?.roomtypeid || null;

  const formatFactorRows = (data, loadedRoomTypes) =>
    data
      .map((factor) => ({
        id: factor.factorid,
        title: factor.factorname,
        factor_type: factor.factor_type || 'service',
        value_type: factor.value_type || 'percentage',
        value: factor.default_value ?? factor.defaultpercentage ?? 0,
        min_guests: factor.min_guests ?? 0,
        show_in_bq: factor.show_in_bq ?? false,
        is_room_amenity: factor.is_room_amenity ?? false,
        auto_apply: factor.auto_apply ?? false,
        description: factor.description || '',
        category_id: factor.category_id ?? null,
        category_name: factor.category_name ?? '',
        per_room_type_values: (factor.per_room_type_values || []).map((v) => {
          const matched = loadedRoomTypes.find((rt) => rt.roomtypename === v.room_type);
          return {
            room_type: v.room_type,
            roomtypeid: v.roomtypeid ?? (matched ? matched.roomtypeid : null),
            value: v.value,
          };
        }),
        isActive: factor.isactive,
      }))
      .sort((a, b) => a.id - b.id);

  const fetchFactors = async () => {
    try {
      const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`);
      const data = await response.json();
      setStats(formatFactorRows(data, roomTypes));
    } catch (error) {
      toast.error('Error fetching factors: ' + error.message);
    }
  };

  const fetchRoomTypes = async () => {
    setRoomTypesLoading(true);
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`);
      const data = await response.json();
      let list = Array.isArray(data) ? data : data.roomtypes || data.room_types || [];
      const normalized = list.map((rt) => ({
        roomtypeid: rt.roomtypeid || rt.id,
        roomtypename: rt.roomtypename || rt.room_type_name || rt.name,
      }));
      setRoomTypes(normalized);
      return normalized;
    } catch (error) {
      toast.error('Could not load room types');
      return [];
    } finally {
      setRoomTypesLoading(false);
    }
  };

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factor-categories`);
      const data = await response.json();
      const normalized = (Array.isArray(data) ? data : []).map((c) => ({
        id: c.id,
        name: c.name,
        description: c.description || '',
        pricing_eligible: c.pricing_eligible || 'optional',
        is_active: !!c.is_active,
      }));
      setCategories(normalized);
      return normalized;
    } catch (error) {
      toast.error('Error fetching categories: ' + error.message);
      return [];
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      const loadedRoomTypes = await fetchRoomTypes();
      await fetchCategories();
      try {
        const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`);
        const data = await response.json();
        setStats(formatFactorRows(data, loadedRoomTypes));
      } catch (error) {
        toast.error('Error fetching factors');
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // Derived Data & Filters
  // ============================================
  const filteredStats = stats.filter((stat) => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      stat.title.toLowerCase().includes(searchLower) ||
      stat.value.toString().includes(searchLower) ||
      (stat.category_name && stat.category_name.toLowerCase().includes(searchLower));

    const matchesCategory = !categoryFilter || String(stat.category_id) === String(categoryFilter);
    const matchesStatus =
      !statusFilter ||
      (statusFilter === 'active' && stat.isActive) ||
      (statusFilter === 'inactive' && !stat.isActive);

    return matchesSearch && matchesCategory && matchesStatus;
  });

  const filteredCategories = useMemo(() => {
    const q = categorySearch.toLowerCase();
    return categories.filter(
      (c) => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
    );
  }, [categories, categorySearch]);

  const getCategoryName = (id) => {
    if (!id) return '';
    return categories.find((x) => x.id === id)?.name || '';
  };

  // ============================================
  // Factor Actions (CRUD)
  // ============================================
  const handleDeleteFactor = (factor) => {
    setFactorToDelete(factor);
    setIsConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!factorToDelete) return;
    try {
      const res = await fetch(
        `${AQ_PRICING_BASE_URL}/pricing/api/delete-factor/${factorToDelete.id}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        setStats((prev) => prev.filter((s) => s.id !== factorToDelete.id));
        toast.success(`"${factorToDelete.title}" deleted`);
      }
    } catch (error) {
      toast.error('Error deleting factor');
    } finally {
      setIsConfirmDeleteOpen(false);
      setFactorToDelete(null);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) setIsConfirmEditOpen(true);
    else {
      setIsEditing(true);
      setEditedStats(JSON.parse(JSON.stringify(stats)));
    }
  };

  const handleSaveEdit = async () => {
    const updatedFactors = editedStats.map((stat) => ({
      factorid: stat.id,
      body: {
        factorname: stat.title,
        factor_type: stat.factor_type,
        value_type: stat.value_type,
        default_value: parseFloat(stat.value) || 0,
        min_guests: parseInt(stat.min_guests) || 0,
        show_in_bq: stat.show_in_bq,
        is_room_amenity: stat.is_room_amenity,
        auto_apply: stat.auto_apply,
        description: stat.description,
        isactive: stat.isActive,
        category_id: stat.category_id ? parseInt(stat.category_id) : null,
        per_room_type_values: (stat.per_room_type_values || [])
          .map((v) => {
            const id = v.roomtypeid ?? getRoomTypeIdByName(v.room_type);
            return id ? { roomtypeid: id, value: parseFloat(v.value) || 0 } : null;
          })
          .filter(Boolean),
      },
    }));

    try {
      await Promise.all(
        updatedFactors.map((f) =>
          fetch(`${AQ_PRICING_BASE_URL}/pricing/api/update-factor/${f.factorid}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(f.body),
          })
        )
      );
      await fetchFactors();
      toast.success('Factors updated successfully');
      setIsConfirmEditOpen(false);
      setIsEditing(false);
    } catch (error) {
      toast.error('Error updating factors');
    }
  };

  const handleFieldChange = (id, field, value) => {
    setEditedStats((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const handleAddRoomTypeValue = (factorId) => {
    setEditedStats((prev) =>
      prev.map((s) => {
        if (s.id !== factorId) return s;
        const existing = s.per_room_type_values || [];
        const next = roomTypes.find(
          (rt) => !existing.map((v) => v.roomtypeid).includes(rt.roomtypeid)
        );
        if (!next) return s;
        return {
          ...s,
          per_room_type_values: [
            ...existing,
            { roomtypeid: next.roomtypeid, room_type: next.roomtypename, value: 0 },
          ],
        };
      })
    );
  };

  const handleRoomTypeValueChange = (factorId, index, field, value) => {
    setEditedStats((prev) =>
      prev.map((s) => {
        if (s.id !== factorId) return s;
        const updated = [...(s.per_room_type_values || [])];
        if (field === 'roomtypeid') {
          const rt = roomTypes.find((r) => r.roomtypeid === parseInt(value));
          updated[index] = {
            ...updated[index],
            roomtypeid: parseInt(value),
            room_type: rt?.roomtypename || '',
          };
        } else {
          updated[index] = { ...updated[index], [field]: value };
        }
        return { ...s, per_room_type_values: updated };
      })
    );
  };

  const handleRemoveRoomTypeValue = (factorId, index) => {
    setEditedStats((prev) =>
      prev.map((s) => {
        if (s.id !== factorId) return s;
        const updated = [...(s.per_room_type_values || [])];
        updated.splice(index, 1);
        return { ...s, per_room_type_values: updated };
      })
    );
  };

  const handleToggleActive = (id) => {
    if (!isEditing) return;
    setEditedStats((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s)));
  };

  const handleNewFactorOpen = () => setIsPopupOpen(true);
  const handleNewFactorClose = () => {
    setIsPopupOpen(false);
    setNewFactor({
      title: '',
      factor_type: 'service',
      value_type: 'percentage',
      default_value: '',
      min_guests: 0,
      show_in_bq: true,
      is_room_amenity: false,
      auto_apply: false,
      isActive: true,
      description: '',
      category_id: '',
      per_room_type_values: [],
    });
  };

  const handleAddNewRoomTypeValue = () => {
    const existing = newFactor.per_room_type_values || [];
    const next = roomTypes.find((rt) => !existing.map((v) => v.roomtypeid).includes(rt.roomtypeid));
    if (!next) return;
    setNewFactor((prev) => ({
      ...prev,
      per_room_type_values: [
        ...existing,
        { roomtypeid: next.roomtypeid, room_type: next.roomtypename, value: 0 },
      ],
    }));
  };

  const handleNewRoomTypeValueChange = (index, field, value) => {
    setNewFactor((prev) => {
      const updated = [...(prev.per_room_type_values || [])];
      if (field === 'roomtypeid') {
        const rt = roomTypes.find((r) => r.roomtypeid === parseInt(value));
        updated[index] = {
          ...updated[index],
          roomtypeid: parseInt(value),
          room_type: rt?.roomtypename || '',
        };
      } else {
        updated[index] = { ...updated[index], [field]: value };
      }
      return { ...prev, per_room_type_values: updated };
    });
  };

  const handleRemoveNewRoomTypeValue = (index) => {
    setNewFactor((prev) => {
      const updated = [...(prev.per_room_type_values || [])];
      updated.splice(index, 1);
      return { ...prev, per_room_type_values: updated };
    });
  };

  const handleNewFactorSave = async () => {
    if (!newFactor.title || newFactor.default_value === '') {
      toast.error('Please fill name and default value');
      return;
    }
    const payload = {
      factorname: newFactor.title,
      factor_type: newFactor.factor_type,
      value_type: newFactor.value_type,
      default_value: parseFloat(newFactor.default_value),
      min_guests: parseInt(newFactor.min_guests) || 0,
      show_in_bq: newFactor.show_in_bq,
      is_room_amenity: newFactor.is_room_amenity,
      auto_apply: newFactor.auto_apply,
      isactive: newFactor.isActive,
      description: newFactor.description,
      category_id: newFactor.category_id ? parseInt(newFactor.category_id) : null,
      per_room_type_values: (newFactor.per_room_type_values || [])
        .map((v) => {
          const id = v.roomtypeid ?? getRoomTypeIdByName(v.room_type);
          return id ? { roomtypeid: id, value: parseFloat(v.value) || 0 } : null;
        })
        .filter(Boolean),
    };
    try {
      const res = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/create-factor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        await fetchFactors();
        handleNewFactorClose();
        toast.success('Factor created');
      }
    } catch (error) {
      toast.error('Error creating factor');
    }
  };

  // ============================================
  // Category Actions (CRUD)
  // ============================================
  const openCreateCategoryModal = () => {
    setCategoryModalMode('create');
    setCategoryForm({ name: '', description: '', pricing_eligible: 'optional', is_active: true });
    setIsCategoryModalOpen(true);
  };

  const openEditCategoryModal = (cat) => {
    setCategoryModalMode('edit');
    setEditingCategoryId(cat.id);
    setCategoryForm({
      name: cat.name,
      description: cat.description,
      pricing_eligible: cat.pricing_eligible,
      is_active: cat.is_active,
    });
    setIsCategoryModalOpen(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) return toast.error('Category name is required');
    const url =
      categoryModalMode === 'create'
        ? `${AQ_PRICING_BASE_URL}/pricing/api/create-factor-category`
        : `${AQ_PRICING_BASE_URL}/pricing/api/update-factor-category/${editingCategoryId}`;
    try {
      const res = await fetch(url, {
        method: categoryModalMode === 'create' ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryForm),
      });
      if (res.ok) {
        toast.success(`Category ${categoryModalMode === 'create' ? 'created' : 'updated'}`);
        setIsCategoryModalOpen(false);
        await fetchCategories();
        await fetchFactors();
      }
    } catch (error) {
      toast.error('Error saving category');
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      const res = await fetch(
        `${AQ_PRICING_BASE_URL}/pricing/api/delete-factor-category/${categoryToDelete.id}`,
        { method: 'DELETE' }
      );
      if (res.ok) {
        toast.success('Category deleted');
        setIsConfirmCategoryDeleteOpen(false);
        await fetchCategories();
        await fetchFactors();
      }
    } catch (error) {
      toast.error('Error deleting category');
    }
  };

  // ============================================
  // Helper UI Functions
  // ============================================
  const getValueDisplay = (stat) => {
    const value = parseFloat(stat.value) || 0;
    const sign = value > 0 ? '+' : '';
    return `${sign}${value}${stat.value_type === 'percentage' ? '%' : '₹'}`;
  };

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      {/* Header — UNCHANGED */}
      <header className="flex justify-between items-center mb-3 bg-white p-3 shadow-sm">
        <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
        <div className="flex space-x-6">
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
            className="btn"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
            className="btn"
          >
            Templates
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
            className="text-black font-bold"
          >
            Factors
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
            className="btn"
          >
            Pricing
          </button>
          <button
            onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
            className="btn"
          >
            Room Rates
          </button>
        </div>
      </header>

      <div className="mx-auto">
        {/* TOP BAR & STATS */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                  Factors Management
                </h1>
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                  Real-time Engine
                </span>
              </div>
              <p className="text-gray-500 text-sm">
                Shared library for Inventory, Commercial, Preference and Operational factors
              </p>
            </div>

            <div className="flex divide-x divide-gray-200 border border-gray-200 rounded-xl bg-gray-50/50">
              <div className="px-6 py-3 text-center">
                <p className="text-sm font-medium text-gray-500 mb-1">Total Factors</p>
                <p className="text-xl font-bold text-gray-900">{stats.length}</p>
              </div>
              <div className="px-6 py-3 text-center">
                <p className="text-sm font-medium text-gray-500 mb-1">Active</p>
                <p className="text-xl font-bold text-green-600">
                  {stats.filter((s) => s.isActive).length}
                </p>
              </div>
              <div className="px-6 py-3 text-center">
                <p className="text-sm font-medium text-gray-500 mb-1">Categories</p>
                <p className="text-xl font-bold text-blue-600">{categories.length}</p>
              </div>
            </div>
          </div>

          {/* TABS */}
          <div className="flex items-center justify-between mt-8 border-b border-gray-200">
            <div className="flex gap-8">
              <button
                onClick={() => setActiveTab('categories')}
                className={`flex items-center gap-2 pb-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'categories'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-900'
                }`}
              >
                <FiFolder size={16} /> Factors Categories
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'categories' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                >
                  {categories.length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('factors')}
                className={`flex items-center gap-2 pb-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'factors'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-900'
                }`}
              >
                <FiSliders size={16} /> Factors Management
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'factors' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
                >
                  {stats.length}
                </span>
              </button>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400 pb-3">
              <FiClock size={14} /> Last updated 10 mins ago
            </div>
          </div>
        </div>

        {/* ===================================================== */}
        {/* TAB: FACTOR CATEGORIES                                */}
        {/* ===================================================== */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="relative w-full sm:w-96">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search categories..."
                  className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 shadow-sm"
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                />
              </div>
              <button
                onClick={openCreateCategoryModal}
                className="w-full sm:w-auto px-6 py-3 bg-gray-900 text-white font-medium rounded-xl hover:bg-black transition-colors flex items-center justify-center gap-2 shadow-sm"
              >
                <FiPlus size={18} /> Create Category
              </button>
            </div>

            {categoriesLoading ? (
              <p className="text-center py-10 text-gray-500">Loading categories...</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCategories.map((cat) => {
                  const catFactors = stats.filter((s) => s.category_id === cat.id);
                  const design = getCategoryDesign(cat.name);

                  return (
                    <div
                      key={cat.id}
                      className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow flex flex-col h-full relative group"
                    >
                      <div className="flex justify-between items-start mb-5">
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center ${design.bg} ${design.color}`}
                        >
                          {design.icon}
                        </div>
                        <span className="bg-gray-50 text-gray-600 text-xs font-semibold px-2.5 py-1 rounded-md border border-gray-100">
                          {catFactors.length} Factors
                        </span>
                      </div>

                      <h3 className="text-lg font-bold text-gray-900 mb-2">{cat.name}</h3>
                      <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                        {cat.description || 'No description provided.'}
                      </p>

                      <div className="flex flex-wrap gap-2 mb-6 mt-auto">
                        {catFactors.slice(0, 4).map((f, i) => (
                          <span
                            key={i}
                            className="text-xs font-medium text-gray-600 bg-white border border-gray-200 px-2.5 py-1 rounded-md"
                          >
                            {f.title}
                          </span>
                        ))}
                        {catFactors.length > 4 && (
                          <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-1 rounded-md">
                            +{catFactors.length - 4} more
                          </span>
                        )}
                        {catFactors.length === 0 && (
                          <span className="text-xs italic text-gray-400">No factors yet</span>
                        )}
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-auto">
                        <span className="text-sm font-medium text-gray-600">
                          {catFactors.filter((f) => f.isActive).length} active rules
                        </span>
                        <div className="flex items-center gap-3">
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => openEditCategoryModal(cat)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              <FiEdit size={16} />
                            </button>
                            <button
                              onClick={() => {
                                setCategoryToDelete(cat);
                                setIsConfirmCategoryDeleteOpen(true);
                              }}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                          <button
                            onClick={() => {
                              setActiveTab('factors');
                              setCategoryFilter(cat.id.toString());
                            }}
                            className="text-sm font-semibold text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          >
                            Manage Factors <FiArrowRight size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ===================================================== */}
        {/* TAB: FACTORS MANAGEMENT                               */}
        {/* ===================================================== */}
        {activeTab === 'factors' && (
          <div className="space-y-6">
            {/* Filter Bar */}
            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-center gap-4">
              <div className="relative flex-1 w-full">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search factor by name, percentage (e.g. 0.05%), or category..."
                  className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full md:w-auto px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none"
              >
                <option value="">All Categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-auto px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="flex gap-2 w-full md:w-auto">
                <button
                  onClick={handleNewFactorOpen}
                  className="flex-1 md:flex-none px-5 py-2.5 bg-gray-900 text-white text-sm font-medium rounded-xl hover:bg-black flex items-center justify-center gap-2"
                >
                  <FiPlus size={16} /> Create New Factor
                </button>
                <button
                  onClick={handleEditToggle}
                  className="flex-1 md:flex-none px-5 py-2.5 border border-gray-200 text-gray-700 text-sm font-medium rounded-xl hover:bg-gray-50 flex items-center justify-center gap-2"
                >
                  <FiEdit size={16} /> {isEditing ? 'Save' : 'Edit Factors'}
                </button>
              </div>
            </div>

            {/* Active Filters (if any) */}
            {categoryFilter && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500">Active Filters:</span>
                <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium text-xs">
                  Category: {getCategoryName(parseInt(categoryFilter))}
                </span>
                <button
                  onClick={() => setCategoryFilter('')}
                  className="text-blue-600 hover:underline text-xs font-medium ml-2"
                >
                  Reset
                </button>
              </div>
            )}

            {/* Factor Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredStats.map((stat) => {
                const editVersion = editedStats.find((s) => s.id === stat.id);
                const currentStat = isEditing ? editVersion : stat;
                const isExpanded = expandedFactor === stat.id;
                const catName =
                  currentStat?.category_name || getCategoryName(currentStat?.category_id);

                return (
                  <div
                    key={stat.id}
                    className={`bg-white rounded-2xl border transition-all flex flex-col ${
                      !currentStat?.isActive
                        ? 'opacity-60 border-gray-100'
                        : 'border-gray-200 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className="p-5 flex-1">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 bg-gray-50 px-2.5 py-1 rounded-md truncate max-w-[120px]">
                          {catName || 'Uncategorized'}
                        </span>
                        {isEditing ? (
                          <button
                            onClick={() => handleToggleActive(stat.id)}
                            className={`w-9 h-5 rounded-full transition-colors ${currentStat?.isActive ? 'bg-black' : 'bg-gray-300'}`}
                          >
                            <span
                              className={`block w-3.5 h-3.5 bg-white rounded-full transition-transform mt-0.5 ${currentStat?.isActive ? 'translate-x-4.5' : 'translate-x-0.5'}`}
                            />
                          </button>
                        ) : (
                          <span
                            className={`flex items-center gap-1.5 text-xs font-medium ${currentStat?.isActive ? 'text-green-700' : 'text-red-700'}`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${currentStat?.isActive ? 'bg-green-500' : 'bg-red-500'}`}
                            ></span>
                            {currentStat?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        )}
                      </div>

                      {isEditing ? (
                        <>
                          <input
                            type="text"
                            value={currentStat?.title || ''}
                            onChange={(e) => handleFieldChange(stat.id, 'title', e.target.value)}
                            className="w-full text-gray-900 font-bold text-lg border-b border-blue-300 focus:outline-none mb-4 bg-blue-50/30 px-1"
                          />
                          <select
                            value={currentStat?.category_id || ''}
                            onChange={(e) =>
                              handleFieldChange(stat.id, 'category_id', e.target.value || null)
                            }
                            className="w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 mb-4"
                          >
                            <option value="">— No category —</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <h3 className="text-gray-900 font-bold text-lg mb-4">
                          {currentStat?.title}
                        </h3>
                      )}

                      <div className="mt-auto">
                        <p className="text-xs text-gray-500 mb-1">Rate Adjustment</p>
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={currentStat?.value_type}
                              onChange={(e) =>
                                handleFieldChange(stat.id, 'value_type', e.target.value)
                              }
                              className="text-sm border rounded px-1 py-1"
                            >
                              <option value="percentage">%</option>
                              <option value="flat">₹</option>
                            </select>
                            <input
                              type="number"
                              value={currentStat?.value || 0}
                              onChange={(e) => handleFieldChange(stat.id, 'value', e.target.value)}
                              className="w-24 text-sm font-bold border rounded px-2 py-1"
                            />
                          </div>
                        ) : (
                          <p
                            className={`text-2xl font-black ${parseFloat(currentStat?.value) < 0 ? 'text-red-600' : 'text-gray-900'}`}
                          >
                            {getValueDisplay(currentStat)}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Expandable section for flags/per-room */}
                    <div className="border-t border-gray-100">
                      <button
                        onClick={() => setExpandedFactor(isExpanded ? null : stat.id)}
                        className="w-full flex items-center justify-between px-5 py-3 text-xs font-medium text-gray-500 hover:text-gray-900 bg-gray-50/50 rounded-b-2xl"
                      >
                        <span>Details & Overrides</span>
                        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                      </button>

                      {isExpanded && (
                        <div className="px-5 py-4 bg-gray-50/50 space-y-3 border-t border-gray-100 rounded-b-2xl">
                          {/* Flags */}
                          <div className="flex flex-wrap gap-1.5">
                            {currentStat?.show_in_bq && (
                              <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded">
                                Guest Visible
                              </span>
                            )}
                            {currentStat?.is_room_amenity && (
                              <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                Room Amenity
                              </span>
                            )}
                            {currentStat?.auto_apply && (
                              <span className="text-[10px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                                Auto Apply
                              </span>
                            )}
                          </div>

                          {/* Per Room Type */}
                          {(currentStat?.per_room_type_values?.length > 0 || isEditing) && (
                            <div className="space-y-2 mt-3 pt-3 border-t border-gray-200">
                              <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
                                Room Overrides
                              </p>
                              {(currentStat?.per_room_type_values || []).map((item, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-xs">
                                  {isEditing ? (
                                    <>
                                      <select
                                        value={item.roomtypeid || ''}
                                        onChange={(e) =>
                                          handleRoomTypeValueChange(
                                            stat.id,
                                            idx,
                                            'roomtypeid',
                                            e.target.value
                                          )
                                        }
                                        className="flex-1 border rounded px-1.5 py-1 text-xs"
                                      >
                                        <option value="">Select</option>
                                        {roomTypes.map((rt) => (
                                          <option key={rt.roomtypeid} value={rt.roomtypeid}>
                                            {rt.roomtypename}
                                          </option>
                                        ))}
                                      </select>
                                      <input
                                        type="number"
                                        value={item.value}
                                        onChange={(e) =>
                                          handleRoomTypeValueChange(
                                            stat.id,
                                            idx,
                                            'value',
                                            parseFloat(e.target.value) || 0
                                          )
                                        }
                                        className="w-16 border rounded px-1.5 py-1 text-xs text-center"
                                      />
                                      <button
                                        onClick={() => handleRemoveRoomTypeValue(stat.id, idx)}
                                        className="text-red-500"
                                      >
                                        <FiTrash2 size={12} />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <span className="flex-1 text-gray-600 truncate">
                                        {item.room_type}
                                      </span>
                                      <span className="font-semibold text-gray-900">
                                        {currentStat.value_type === 'percentage'
                                          ? `${item.value}%`
                                          : `₹${item.value}`}
                                      </span>
                                    </>
                                  )}
                                </div>
                              ))}
                              {isEditing && (
                                <button
                                  onClick={() => handleAddRoomTypeValue(stat.id)}
                                  className="text-[11px] font-medium text-blue-600 flex items-center gap-1 mt-1"
                                >
                                  <FiPlus size={10} /> Add Override
                                </button>
                              )}
                            </div>
                          )}

                          {isEditing && (
                            <button
                              onClick={() => handleDeleteFactor(stat)}
                              className="w-full mt-2 py-1.5 bg-red-50 text-red-600 rounded text-xs font-medium flex items-center justify-center gap-1 hover:bg-red-100"
                            >
                              <FiTrash2 size={12} /> Delete Factor
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {filteredStats.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-200">
                <p className="text-gray-500">No factors found matching your filters.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ====================================== */}
      {/* MODALS                                 */}
      {/* ====================================== */}

      {/* CREATE FACTOR MODAL */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4 overflow-y-auto backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8">
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Create New Factor</h3>
                <p className="text-gray-500 text-sm mt-1">
                  Configure a pricing factor for your property
                </p>
              </div>
              <button
                onClick={handleNewFactorClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5 max-h-[65vh] overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                    Factor Name *
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="e.g., AC, Breakfast, Diwali Premium"
                    value={newFactor.title}
                    onChange={(e) => setNewFactor({ ...newFactor, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                    Factor Type *
                  </label>
                  <select
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={newFactor.factor_type}
                    onChange={(e) => setNewFactor({ ...newFactor, factor_type: e.target.value })}
                  >
                    {FACTOR_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                  Factor Category
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  value={newFactor.category_id}
                  onChange={(e) => setNewFactor({ ...newFactor, category_id: e.target.value })}
                >
                  <option value="">— No category —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                  Description (Optional)
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="Short note"
                  value={newFactor.description}
                  onChange={(e) => setNewFactor({ ...newFactor, description: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                    Value Type *
                  </label>
                  <select
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    value={newFactor.value_type}
                    onChange={(e) => setNewFactor({ ...newFactor, value_type: e.target.value })}
                  >
                    {VALUE_TYPES.map((v) => (
                      <option key={v.value} value={v.value}>
                        {v.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                    Default Value *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder={newFactor.value_type === 'flat' ? '400' : '10'}
                    value={newFactor.default_value}
                    onChange={(e) => setNewFactor({ ...newFactor, default_value: e.target.value })}
                  />
                </div>
              </div>

              {newFactor.factor_type === 'auto' && (
                <div className="border border-purple-200 bg-purple-50 rounded-xl p-4">
                  <label className="block text-gray-900 text-sm font-semibold mb-1">
                    Included Guests (Free) *
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Charges apply only for guests beyond this number.
                  </p>
                  <input
                    type="number"
                    min="0"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none text-sm"
                    placeholder="e.g., 2"
                    value={newFactor.min_guests}
                    onChange={(e) => setNewFactor({ ...newFactor, min_guests: e.target.value })}
                  />
                </div>
              )}

              <div className="border border-gray-200 rounded-xl p-4">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <label className="block text-gray-900 text-sm font-semibold">
                      Per-Room-Type Values (Optional)
                    </label>
                    <p className="text-xs text-gray-500">
                      Override default value for specific room types
                    </p>
                  </div>
                  <button
                    onClick={handleAddNewRoomTypeValue}
                    className="text-xs font-medium text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100 flex items-center gap-1"
                  >
                    <FiPlus size={12} /> Add
                  </button>
                </div>
                {newFactor.per_room_type_values.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No overrides added.</p>
                ) : (
                  <div className="space-y-2">
                    {newFactor.per_room_type_values.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <select
                          value={item.roomtypeid || ''}
                          onChange={(e) =>
                            handleNewRoomTypeValueChange(idx, 'roomtypeid', e.target.value)
                          }
                          className="flex-1 border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
                        >
                          <option value="">Select Room Type</option>
                          {roomTypes.map((rt) => (
                            <option key={rt.roomtypeid} value={rt.roomtypeid}>
                              {rt.roomtypename}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          step="0.01"
                          value={item.value}
                          onChange={(e) =>
                            handleNewRoomTypeValueChange(
                              idx,
                              'value',
                              parseFloat(e.target.value) || 0
                            )
                          }
                          className="w-24 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-center"
                          placeholder="Value"
                        />
                        <button
                          onClick={() => handleRemoveNewRoomTypeValue(idx)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-gray-200 rounded-xl p-4 space-y-4">
                <label className="block text-gray-900 text-sm font-semibold">Behavior Flags</label>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Show on Booking Page</p>
                    <p className="text-xs text-gray-500">Guests can select this as an add-on</p>
                  </div>
                  <Toggle
                    checked={newFactor.show_in_bq}
                    onChange={() => setNewFactor((p) => ({ ...p, show_in_bq: !p.show_in_bq }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Room Amenity</p>
                    <p className="text-xs text-gray-500">Appears in Room Management</p>
                  </div>
                  <Toggle
                    checked={newFactor.is_room_amenity}
                    onChange={() =>
                      setNewFactor((p) => ({ ...p, is_room_amenity: !p.is_room_amenity }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">Auto-Apply</p>
                    <p className="text-xs text-gray-500">
                      Applied automatically based on conditions
                    </p>
                  </div>
                  <Toggle
                    checked={newFactor.auto_apply}
                    onChange={() => setNewFactor((p) => ({ ...p, auto_apply: !p.auto_apply }))}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <div>
                  <label className="block text-gray-900 text-sm font-semibold">Factor Status</label>
                  <p className="text-gray-500 text-xs">
                    {newFactor.isActive ? 'Active — used in calculations' : 'Inactive'}
                  </p>
                </div>
                <Toggle
                  checked={newFactor.isActive}
                  onChange={() => setNewFactor((p) => ({ ...p, isActive: !p.isActive }))}
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button
                onClick={handleNewFactorClose}
                className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleNewFactorSave}
                className="px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black font-medium text-sm"
              >
                Create Factor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE / EDIT CATEGORY MODAL */}
      {isCategoryModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  {categoryModalMode === 'create' ? 'Create New Category' : 'Edit Category'}
                </h3>
                <p className="text-gray-500 text-sm mt-1">Group factors under a named category</p>
              </div>
              <button
                onClick={() => setIsCategoryModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                  Category Name *
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="e.g., Seasonal & Time"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                  Description
                </label>
                <textarea
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="What does this category group?"
                  value={categoryForm.description}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, description: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1.5">
                  Pricing Eligible *
                </label>
                <select
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  value={categoryForm.pricing_eligible}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, pricing_eligible: e.target.value })
                  }
                >
                  {PRICING_ELIGIBLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div>
                  <label className="block text-gray-900 text-sm font-semibold">
                    Category Status
                  </label>
                  <p className="text-gray-500 text-xs">
                    {categoryForm.is_active ? 'Active' : 'Inactive'}
                  </p>
                </div>
                <Toggle
                  checked={categoryForm.is_active}
                  onChange={() => setCategoryForm((p) => ({ ...p, is_active: !p.is_active }))}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl">
              <button
                onClick={() => setIsCategoryModalOpen(false)}
                className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCategory}
                className="px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black font-medium text-sm"
              >
                {categoryModalMode === 'create' ? 'Create' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE FACTOR */}
      {isConfirmDeleteOpen && factorToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Factor</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to delete{' '}
              <strong className="text-black">"{factorToDelete.title}"</strong>? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setIsConfirmDeleteOpen(false)}
                className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM EDIT FACTORS */}
      {isConfirmEditOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Save Changes</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to save all modifications to the factors?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsConfirmEditOpen(false)}
                className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-black font-medium text-sm"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DELETE CATEGORY */}
      {isConfirmCategoryDeleteOpen && categoryToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Delete Category</h2>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete{' '}
              <strong className="text-black">"{categoryToDelete.name}"</strong>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Factors linked to this category will be set to <strong>No category</strong>.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsConfirmCategoryDeleteOpen(false)}
                className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 font-medium text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDeleteCategory}
                className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer className="mt-12" />
    </div>
  );
};

export default ModifyFactors;

// import React, { useState, useEffect } from 'react';
// import {
//   FiSearch,
//   FiEdit,
//   FiTrash2,
//   FiPlus,
//   FiSave,
//   FiX,
//   FiChevronDown,
//   FiChevronUp,
// } from 'react-icons/fi';
// import { useNavigate } from 'react-router-dom';
// import { toast, ToastContainer } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const AQ_PRICING_BASE_URL = process.env.REACT_APP_AQ_PRICING_BASE_URL;
// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// // ============================================
// // Enums for factor configuration
// // ============================================
// const FACTOR_TYPES = [
//   { value: 'amenity', label: 'Room Amenity (Geyser, TV, etc.)' },
//   { value: 'ac', label: 'Air Conditioning (AC / Non-AC)' },
//   { value: 'bedtype', label: 'Bed Type (Queen, Twin, King, etc.)' },
//   { value: 'service', label: 'Guest Service (Breakfast, Airport Drop, etc.)' },
//   { value: 'discount', label: 'Promotions & Discount' },
//   { value: 'auto', label: 'Auto-Calculated (Extra Guest, etc.)' },
//   { value: 'seasonal', label: 'Seasonal (Diwali, Weekend, etc.)' },
//   { value: 'event', label: 'Event (Concert, Festival, etc.)' },
// ];

// const VALUE_TYPES = [
//   { value: 'flat', label: 'Flat Amount (₹)' },
//   { value: 'percentage', label: 'Percentage (%)' },
// ];

// const ModifyFactors = () => {
//   const [stats, setStats] = useState([]);
//   const [isEditing, setIsEditing] = useState(false);
//   const [isPopupOpen, setIsPopupOpen] = useState(false);
//   const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
//   const [factorToDelete, setFactorToDelete] = useState(null);
//   const [editedStats, setEditedStats] = useState([]);
//   const [isConfirmEditOpen, setIsConfirmEditOpen] = useState(false);
//   const [searchQuery, setSearchQuery] = useState('');
//   const [roomTypes, setRoomTypes] = useState([]);
//   const [roomTypesLoading, setRoomTypesLoading] = useState(false);
//   const [expandedFactor, setExpandedFactor] = useState(null);

//   const [newFactor, setNewFactor] = useState({
//     title: '',
//     factor_type: 'service',
//     value_type: 'flat',
//     default_value: '',
//     min_guests: 0, // ⬅️ NEW
//     show_in_bq: true,
//     is_room_amenity: false,
//     auto_apply: false,
//     isActive: true,
//     description: '',
//     per_room_type_values: [],
//   });

//   const navigate = useNavigate();

//   // ============================================
//   // Helper: resolve roomtypename → roomtypeid
//   // ============================================
//   const getRoomTypeIdByName = (name) => {
//     const rt = roomTypes.find((r) => r.roomtypename === name);
//     return rt ? rt.roomtypeid : null;
//   };

//   const getRoomTypeNameById = (id) => {
//     const rt = roomTypes.find((r) => r.roomtypeid === id);
//     return rt ? rt.roomtypename : '';
//   };

//   // ============================================
//   // FETCH FACTORS & ROOM TYPES
//   // ============================================
//   const fetchFactors = async () => {
//     try {
//       const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`);
//       const data = await response.json();

//       const formattedData = data
//         .map((factor) => ({
//           id: factor.factorid,
//           title: factor.factorname,
//           factor_type: factor.factor_type || 'service',
//           value_type: factor.value_type || 'percentage',
//           value: factor.default_value ?? factor.defaultpercentage ?? 0,
//           min_guests: factor.min_guests ?? 0, // ⬅️ NEW
//           show_in_bq: factor.show_in_bq ?? false,
//           is_room_amenity: factor.is_room_amenity ?? false,
//           auto_apply: factor.auto_apply ?? false,
//           description: factor.description || '',
//           per_room_type_values: (factor.per_room_type_values || []).map((v) => ({
//             room_type: v.room_type,
//             roomtypeid: v.roomtypeid ?? getRoomTypeIdByName(v.room_type),
//             value: v.value,
//           })),
//           isActive: factor.isactive,
//         }))
//         .sort((a, b) => a.id - b.id);

//       setStats(formattedData);
//     } catch (error) {
//       toast.error('Error fetching factors: ' + error.message);
//     }
//   };

//   const fetchRoomTypes = async () => {
//     setRoomTypesLoading(true);
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/roomtypes/`, {
//         method: 'GET',
//         headers: { accept: 'application/json' },
//       });
//       const data = await response.json();

//       let list = [];
//       if (Array.isArray(data)) {
//         list = data;
//       } else if (data && Array.isArray(data.roomtypes)) {
//         list = data.roomtypes;
//       } else if (data && Array.isArray(data.room_types)) {
//         list = data.room_types;
//       }

//       const normalized = list.map((rt) => ({
//         roomtypeid: rt.roomtypeid || rt.id,
//         roomtypename: rt.roomtypename || rt.room_type_name || rt.name,
//       }));

//       setRoomTypes(normalized);
//       return normalized;
//     } catch (error) {
//       console.error('Error fetching room types:', error);
//       toast.error('Could not load room types');
//       setRoomTypes([]);
//       return [];
//     } finally {
//       setRoomTypesLoading(false);
//     }
//   };

//   useEffect(() => {
//     const init = async () => {
//       const loadedRoomTypes = await fetchRoomTypes();
//       // After loading room types, fetch factors so we can map names → IDs
//       try {
//         const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/get-factors`);
//         const data = await response.json();

//         const formattedData = data
//           .map((factor) => ({
//             id: factor.factorid,
//             title: factor.factorname,
//             factor_type: factor.factor_type || 'service',
//             value_type: factor.value_type || 'percentage',
//             value: factor.default_value ?? factor.defaultpercentage ?? 0,
//             min_guests: factor.min_guests ?? 0, // ⬅️ NEW
//             show_in_bq: factor.show_in_bq ?? false,
//             is_room_amenity: factor.is_room_amenity ?? false,
//             auto_apply: factor.auto_apply ?? false,
//             description: factor.description || '',
//             per_room_type_values: (factor.per_room_type_values || []).map((v) => {
//               const matched = loadedRoomTypes.find((rt) => rt.roomtypename === v.room_type);
//               return {
//                 room_type: v.room_type,
//                 roomtypeid: v.roomtypeid ?? (matched ? matched.roomtypeid : null),
//                 value: v.value,
//               };
//             }),
//             isActive: factor.isactive,
//           }))
//           .sort((a, b) => a.id - b.id);

//         setStats(formattedData);
//       } catch (error) {
//         toast.error('Error fetching factors: ' + error.message);
//       }
//     };

//     init();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const handleSearchChange = (e) => setSearchQuery(e.target.value);

//   const filteredStats = stats.filter((stat) => {
//     const searchLower = searchQuery.toLowerCase();
//     return (
//       stat.title.toLowerCase().includes(searchLower) || stat.value.toString().includes(searchLower)
//     );
//   });

//   // ============ DELETE ============
//   const handleDeleteFactor = (factor) => {
//     setFactorToDelete(factor);
//     setIsConfirmDeleteOpen(true);
//   };

//   const handleConfirmDelete = async () => {
//     if (!factorToDelete) return;

//     try {
//       const response = await fetch(
//         `${AQ_PRICING_BASE_URL}/pricing/api/delete-factor/${factorToDelete.id}`,
//         {
//           method: 'DELETE',
//           headers: { accept: 'application/json' },
//         }
//       );

//       const data = await response.json();

//       if (response.ok) {
//         setStats((prev) => prev.filter((s) => s.id !== factorToDelete.id));
//         toast.success(`"${factorToDelete.title}" deleted successfully`);
//       } else {
//         toast.error(`Failed to delete factor: ${data.message}`);
//       }
//     } catch (error) {
//       toast.error('Error with DELETE request: ' + error.message);
//     } finally {
//       setIsConfirmDeleteOpen(false);
//       setFactorToDelete(null);
//     }
//   };

//   const handleCancelDelete = () => {
//     setIsConfirmDeleteOpen(false);
//     setFactorToDelete(null);
//   };

//   // ============ EDIT ============
//   const handleEditToggle = () => {
//     if (isEditing) {
//       setIsConfirmEditOpen(true);
//     } else {
//       setIsEditing(true);
//       setEditedStats(JSON.parse(JSON.stringify(stats)));
//     }
//   };

//   const handleSaveEdit = async () => {
//     const updatedFactors = editedStats.map((stat) => ({
//       factorid: stat.id,
//       body: {
//         factorname: stat.title,
//         factor_type: stat.factor_type,
//         value_type: stat.value_type,
//         default_value: parseFloat(stat.value) || 0,
//         min_guests: parseInt(stat.min_guests) || 0, // ⬅️ NEW
//         show_in_bq: stat.show_in_bq,
//         is_room_amenity: stat.is_room_amenity,
//         auto_apply: stat.auto_apply,
//         description: stat.description,
//         isactive: stat.isActive,
//         per_room_type_values: (stat.per_room_type_values || [])
//           .map((v) => {
//             const id = v.roomtypeid ?? getRoomTypeIdByName(v.room_type);
//             if (!id) return null;
//             return {
//               roomtypeid: id,
//               value: parseFloat(v.value) || 0,
//             };
//           })
//           .filter(Boolean),
//       },
//     }));

//     let successCount = 0;
//     let errorCount = 0;

//     try {
//       const updatePromises = updatedFactors.map(async (factor) => {
//         const response = await fetch(
//           `${AQ_PRICING_BASE_URL}/pricing/api/update-factor/${factor.factorid}`,
//           {
//             method: 'PUT',
//             headers: {
//               accept: 'application/json',
//               'Content-Type': 'application/json',
//             },
//             body: JSON.stringify(factor.body),
//           }
//         );

//         if (response.ok) {
//           successCount++;
//         } else {
//           errorCount++;
//           const data = await response.json().catch(() => ({}));
//           console.error('Update failed:', data);
//         }
//       });

//       await Promise.all(updatePromises);

//       // Always refetch for server truth
//       await fetchFactors();

//       if (errorCount === 0) {
//         toast.success('All factors updated successfully');
//       } else {
//         toast.error(`${errorCount} factors failed to update`);
//       }

//       setIsConfirmEditOpen(false);
//       setIsEditing(false);
//     } catch (error) {
//       toast.error('Error with PUT request: ' + error.message);
//     }
//   };

//   const handleCancelEdit = () => {
//     setEditedStats(stats);
//     setIsConfirmEditOpen(false);
//     setIsEditing(false);
//   };

//   const handleFieldChange = (id, field, value) => {
//     setEditedStats((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
//   };

//   // Per-room-type value handlers (edit mode)
//   const handleAddRoomTypeValue = (factorId) => {
//     setEditedStats((prev) =>
//       prev.map((s) => {
//         if (s.id !== factorId) return s;
//         const existing = s.per_room_type_values || [];
//         const usedIds = existing.map((v) => v.roomtypeid);
//         const nextAvailable = roomTypes.find((rt) => !usedIds.includes(rt.roomtypeid));
//         if (!nextAvailable) return s;
//         return {
//           ...s,
//           per_room_type_values: [
//             ...existing,
//             {
//               roomtypeid: nextAvailable.roomtypeid,
//               room_type: nextAvailable.roomtypename,
//               value: 0,
//             },
//           ],
//         };
//       })
//     );
//   };

//   const handleRoomTypeValueChange = (factorId, index, field, value) => {
//     setEditedStats((prev) =>
//       prev.map((s) => {
//         if (s.id !== factorId) return s;
//         const updated = [...(s.per_room_type_values || [])];

//         if (field === 'roomtypeid') {
//           const selectedId = parseInt(value);
//           const rt = roomTypes.find((r) => r.roomtypeid === selectedId);
//           updated[index] = {
//             ...updated[index],
//             roomtypeid: selectedId,
//             room_type: rt ? rt.roomtypename : '',
//           };
//         } else {
//           updated[index] = { ...updated[index], [field]: value };
//         }

//         return { ...s, per_room_type_values: updated };
//       })
//     );
//   };

//   const handleRemoveRoomTypeValue = (factorId, index) => {
//     setEditedStats((prev) =>
//       prev.map((s) => {
//         if (s.id !== factorId) return s;
//         const updated = [...(s.per_room_type_values || [])];
//         updated.splice(index, 1);
//         return { ...s, per_room_type_values: updated };
//       })
//     );
//   };

//   const handleToggleActive = (id) => {
//     if (!isEditing) return;
//     setEditedStats((prev) => prev.map((s) => (s.id === id ? { ...s, isActive: !s.isActive } : s)));
//   };

//   // ============ CREATE ============
//   const handleNewFactorOpen = () => {
//     setIsPopupOpen(true);
//   };

//   const handleNewFactorClose = () => {
//     setIsPopupOpen(false);
//     setNewFactor({
//       title: '',
//       factor_type: 'service',
//       value_type: 'flat',
//       default_value: '',
//       min_guests: 0, // ⬅️ NEW
//       show_in_bq: true,
//       is_room_amenity: false,
//       auto_apply: false,
//       isActive: true,
//       description: '',
//       per_room_type_values: [],
//     });
//   };

//   const handleAddNewRoomTypeValue = () => {
//     const existing = newFactor.per_room_type_values || [];
//     const usedIds = existing.map((v) => v.roomtypeid);
//     const nextAvailable = roomTypes.find((rt) => !usedIds.includes(rt.roomtypeid));
//     if (!nextAvailable) return;
//     setNewFactor((prev) => ({
//       ...prev,
//       per_room_type_values: [
//         ...existing,
//         {
//           roomtypeid: nextAvailable.roomtypeid,
//           room_type: nextAvailable.roomtypename,
//           value: 0,
//         },
//       ],
//     }));
//   };

//   const handleNewRoomTypeValueChange = (index, field, value) => {
//     setNewFactor((prev) => {
//       const updated = [...(prev.per_room_type_values || [])];

//       if (field === 'roomtypeid') {
//         const selectedId = parseInt(value);
//         const rt = roomTypes.find((r) => r.roomtypeid === selectedId);
//         updated[index] = {
//           ...updated[index],
//           roomtypeid: selectedId,
//           room_type: rt ? rt.roomtypename : '',
//         };
//       } else {
//         updated[index] = { ...updated[index], [field]: value };
//       }

//       return { ...prev, per_room_type_values: updated };
//     });
//   };

//   const handleRemoveNewRoomTypeValue = (index) => {
//     setNewFactor((prev) => {
//       const updated = [...(prev.per_room_type_values || [])];
//       updated.splice(index, 1);
//       return { ...prev, per_room_type_values: updated };
//     });
//   };

//   const handleNewFactorSave = async () => {
//     if (!newFactor.title || newFactor.default_value === '') {
//       toast.error('Please fill name and default value');
//       return;
//     }

//     const factorData = {
//       factorname: newFactor.title,
//       factor_type: newFactor.factor_type,
//       value_type: newFactor.value_type,
//       default_value: parseFloat(newFactor.default_value),
//       min_guests: parseInt(newFactor.min_guests) || 0, // ⬅️ NEW
//       show_in_bq: newFactor.show_in_bq,
//       is_room_amenity: newFactor.is_room_amenity,
//       auto_apply: newFactor.auto_apply,
//       isactive: newFactor.isActive,
//       description: newFactor.description,
//       per_room_type_values: (newFactor.per_room_type_values || [])
//         .map((v) => {
//           const id = v.roomtypeid ?? getRoomTypeIdByName(v.room_type);
//           if (!id) return null;
//           return {
//             roomtypeid: id,
//             value: parseFloat(v.value) || 0,
//           };
//         })
//         .filter(Boolean),
//     };

//     try {
//       const response = await fetch(`${AQ_PRICING_BASE_URL}/pricing/api/create-factor`, {
//         method: 'POST',
//         headers: {
//           accept: 'application/json',
//           'Content-Type': 'application/json',
//         },
//         body: JSON.stringify(factorData),
//       });

//       const data = await response.json();

//       if (response.ok) {
//         await fetchFactors();
//         handleNewFactorClose();
//         toast.success('New factor created successfully!');
//       } else {
//         toast.error('Error creating factor: ' + (data.detail || data.message));
//       }
//     } catch (error) {
//       toast.error('Error with fetch: ' + error.message);
//     }
//   };

//   // ============ HELPERS ============
//   const getFactorTypeLabel = (type) => {
//     const found = FACTOR_TYPES.find((t) => t.value === type);
//     return found ? found.label.split(' (')[0] : 'Service';
//   };

//   const getFactorTypeColor = (type) => {
//     switch (type) {
//       case 'amenity':
//         return 'bg-blue-100 text-blue-800';
//       case 'service':
//         return 'bg-green-100 text-green-800';
//       case 'auto':
//         return 'bg-purple-100 text-purple-800';
//       case 'seasonal':
//         return 'bg-orange-100 text-orange-800';
//       case 'event':
//         return 'bg-pink-100 text-pink-800';
//       default:
//         return 'bg-gray-100 text-gray-800';
//     }
//   };

//   const getValueDisplay = (stat) => {
//     const value = parseFloat(stat.value) || 0;
//     if (stat.value_type === 'percentage') {
//       return `${value}%`;
//     }
//     return `₹${value.toFixed(2)}`;
//   };

//   return (
//     <div className="bg-gray-100 h-full p-6">
//       <header className="flex justify-between items-center mb-3 bg-white p-3">
//         <h1 className="text-black text-xl font-bold ml-2">Pricing Service</h1>
//         <div className="flex space-x-6">
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-overview')}
//             className="btn"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing-template')}
//             className="btn"
//           >
//             Templates
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/modify-factors')}
//             className="text-black font-bold"
//           >
//             Factors
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/pricing')}
//             className="btn"
//           >
//             Pricing
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/core-modules/revenue-management/room-rates')}
//             className="btn"
//           >
//             Room Rates
//           </button>
//         </div>
//       </header>

//       <div className="bg-white p-6 mt-2">
//         <h1 className="text-xl font-bold text-center text-black">Pricing Factors</h1>
//         <p className="text-sm font-poppins font-semibold text-center mb-1 text-black">
//           Configure factors for pricing — amenities, services, seasonal adjustments, and
//           auto-calculated values.
//         </p>
//         <hr className="border border-gray-300 w-2/6 mx-auto mb-5" />

//         {/* Action Bar */}
//         <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
//           <div className="w-full sm:w-1/3 relative">
//             <input
//               type="text"
//               className="w-full px-4 py-2 text-md pl-10 bg-white text-black border border-gray-300 rounded-lg outline-none"
//               placeholder="Search by name or value"
//               value={searchQuery}
//               onChange={handleSearchChange}
//             />
//             <FiSearch
//               size={15}
//               className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
//             />
//           </div>

//           <div className="flex flex-wrap gap-3">
//             <button
//               onClick={handleNewFactorOpen}
//               className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
//             >
//               <FiPlus size={16} />
//               Create New Factor
//             </button>

//             <button
//               onClick={handleEditToggle}
//               className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center gap-2 font-medium"
//             >
//               {isEditing ? <FiSave size={16} /> : <FiEdit size={16} />}
//               {isEditing ? 'Save Changes' : 'Edit Factors'}
//             </button>
//           </div>
//         </div>

//         {/* Factor Cards */}
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
//           {filteredStats.map((stat) => {
//             const editVersion = editedStats.find((s) => s.id === stat.id);
//             const currentStat = isEditing ? editVersion : stat;
//             const isExpanded = expandedFactor === stat.id;
//             const hasPerRoomValues =
//               currentStat?.per_room_type_values && currentStat.per_room_type_values.length > 0;

//             return (
//               <div
//                 key={stat.id}
//                 className={`bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all ${
//                   !currentStat?.isActive ? 'opacity-60' : ''
//                 }`}
//               >
//                 <div className="flex justify-between items-start mb-2">
//                   <span
//                     className={`text-xs font-semibold px-2 py-1 rounded ${getFactorTypeColor(
//                       currentStat?.factor_type
//                     )}`}
//                   >
//                     {getFactorTypeLabel(currentStat?.factor_type)}
//                   </span>

//                   {isEditing ? (
//                     <button
//                       onClick={() => handleToggleActive(stat.id)}
//                       className={`w-10 h-5 rounded-full transition-colors ${
//                         currentStat?.isActive ? 'bg-black' : 'bg-gray-300'
//                       }`}
//                     >
//                       <span
//                         className={`block w-3.5 h-3.5 bg-white rounded-full transition-transform mt-0.5 ${
//                           currentStat?.isActive ? 'translate-x-5' : 'translate-x-0.5'
//                         }`}
//                       />
//                     </button>
//                   ) : (
//                     <span
//                       className={`text-xs px-2 py-0.5 rounded-full ${
//                         currentStat?.isActive
//                           ? 'bg-green-100 text-green-800'
//                           : 'bg-red-100 text-red-800'
//                       }`}
//                     >
//                       {currentStat?.isActive ? 'Active' : 'Inactive'}
//                     </span>
//                   )}
//                 </div>

//                 {isEditing ? (
//                   <input
//                     type="text"
//                     value={currentStat?.title || ''}
//                     onChange={(e) => handleFieldChange(stat.id, 'title', e.target.value)}
//                     className="w-full text-black font-semibold text-base border-b border-dashed border-gray-300 outline-none mb-2"
//                   />
//                 ) : (
//                   <h3 className="text-black font-semibold text-base mb-2 line-clamp-2">
//                     {currentStat?.title}
//                   </h3>
//                 )}

//                 <div className="flex items-center gap-2 mb-2 flex-wrap">
//                   {isEditing ? (
//                     <>
//                       <select
//                         value={currentStat?.value_type}
//                         onChange={(e) => handleFieldChange(stat.id, 'value_type', e.target.value)}
//                         className="text-xs border rounded px-1 py-1"
//                       >
//                         {VALUE_TYPES.map((v) => (
//                           <option key={v.value} value={v.value}>
//                             {v.value === 'flat' ? '₹' : '%'}
//                           </option>
//                         ))}
//                       </select>
//                       <input
//                         type="number"
//                         value={currentStat?.value || 0}
//                         onChange={(e) => handleFieldChange(stat.id, 'value', e.target.value)}
//                         className="w-24 text-sm border rounded px-2 py-1 text-center"
//                       />
//                       <span className="text-xs text-gray-500">default</span>

//                       {/* ⬅️ NEW: Inline min_guests editor for auto factors */}
//                       {currentStat?.factor_type === 'auto' && (
//                         <>
//                           <span className="text-xs text-gray-500">·</span>
//                           <span className="text-xs text-gray-500">after</span>
//                           <input
//                             type="number"
//                             min="0"
//                             step="1"
//                             value={currentStat?.min_guests ?? 0}
//                             onChange={(e) =>
//                               handleFieldChange(stat.id, 'min_guests', e.target.value)
//                             }
//                             className="w-16 text-sm border rounded px-2 py-1 text-center"
//                           />
//                           <span className="text-xs text-gray-500">guests</span>
//                         </>
//                       )}
//                     </>
//                   ) : (
//                     <>
//                       <span className="text-2xl font-bold text-black">
//                         {getValueDisplay(currentStat)}
//                       </span>
//                       {currentStat?.factor_type === 'auto' && currentStat?.min_guests > 0 && (
//                         <span className="text-xs text-gray-600 italic">
//                           (after {currentStat.min_guests} guests)
//                         </span>
//                       )}
//                     </>
//                   )}
//                 </div>

//                 <div className="flex flex-wrap gap-1 mb-2">
//                   {currentStat?.show_in_bq && (
//                     <span className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">
//                       Guest-visible
//                     </span>
//                   )}
//                   {currentStat?.is_room_amenity && (
//                     <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
//                       Room Amenity
//                     </span>
//                   )}
//                   {currentStat?.auto_apply && (
//                     <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
//                       Auto-Apply
//                     </span>
//                   )}
//                   {/* ⬅️ NEW: min_guests badge for auto factors */}
//                   {currentStat?.factor_type === 'auto' && currentStat?.min_guests > 0 && (
//                     <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded">
//                       After {currentStat.min_guests} guests
//                     </span>
//                   )}
//                 </div>

//                 {(hasPerRoomValues || isEditing) && (
//                   <button
//                     onClick={() => setExpandedFactor(isExpanded ? null : stat.id)}
//                     className="w-full flex items-center justify-between text-xs text-gray-600 hover:text-black py-1 border-t border-gray-100"
//                   >
//                     <span>
//                       Per-Room-Type Values ({currentStat?.per_room_type_values?.length || 0})
//                     </span>
//                     {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
//                   </button>
//                 )}

//                 {isExpanded && (
//                   <div className="mt-2 space-y-2">
//                     {(currentStat?.per_room_type_values || []).map((item, idx) => (
//                       <div key={idx} className="flex items-center gap-2 text-xs">
//                         {isEditing ? (
//                           <>
//                             <select
//                               value={item.roomtypeid || ''}
//                               onChange={(e) =>
//                                 handleRoomTypeValueChange(
//                                   stat.id,
//                                   idx,
//                                   'roomtypeid',
//                                   e.target.value
//                                 )
//                               }
//                               className="flex-1 border rounded px-2 py-1"
//                             >
//                               <option value="">-- Select --</option>
//                               {roomTypes.map((rt) => (
//                                 <option key={rt.roomtypeid} value={rt.roomtypeid}>
//                                   {rt.roomtypename}
//                                 </option>
//                               ))}
//                             </select>
//                             <input
//                               type="number"
//                               value={item.value}
//                               onChange={(e) =>
//                                 handleRoomTypeValueChange(
//                                   stat.id,
//                                   idx,
//                                   'value',
//                                   parseFloat(e.target.value) || 0
//                                 )
//                               }
//                               className="w-20 border rounded px-2 py-1 text-center"
//                             />
//                             <button
//                               onClick={() => handleRemoveRoomTypeValue(stat.id, idx)}
//                               className="text-red-500 hover:text-red-700"
//                             >
//                               <FiTrash2 size={12} />
//                             </button>
//                           </>
//                         ) : (
//                           <>
//                             <span className="flex-1 text-gray-700">{item.room_type}</span>
//                             <span className="font-semibold text-gray-900">
//                               {currentStat.value_type === 'percentage'
//                                 ? `${item.value}%`
//                                 : `₹${item.value}`}
//                             </span>
//                           </>
//                         )}
//                       </div>
//                     ))}

//                     {isEditing && (
//                       <button
//                         onClick={() => handleAddRoomTypeValue(stat.id)}
//                         className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
//                       >
//                         <FiPlus size={12} /> Add Room Type Value
//                       </button>
//                     )}
//                   </div>
//                 )}

//                 {isEditing && (
//                   <div className="flex justify-end mt-3">
//                     <button
//                       onClick={() => handleDeleteFactor(stat)}
//                       className="p-1.5 text-white bg-red-500 rounded text-xs flex items-center gap-1"
//                     >
//                       <FiTrash2 size={12} />
//                       Delete
//                     </button>
//                   </div>
//                 )}
//               </div>
//             );
//           })}
//         </div>

//         {filteredStats.length === 0 && (
//           <p className="text-center text-gray-500 py-8">
//             No factors found. Click "Create New Factor" to add one.
//           </p>
//         )}
//       </div>

//       {/* DELETE CONFIRM */}
//       {isConfirmDeleteOpen && factorToDelete && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
//             <div className="p-6">
//               <h2 className="text-xl font-semibold text-gray-900 mb-2">Delete Factor</h2>
//               <p className="text-gray-600 text-lg mb-2">
//                 Are you sure you want to delete this factor?
//               </p>
//               <p className="text-black font-medium text-lg mb-4">"{factorToDelete.title}"</p>
//               <p className="text-gray-500 text-sm mb-6">This action cannot be undone.</p>
//               <div className="flex justify-end space-x-3">
//                 <button
//                   onClick={handleCancelDelete}
//                   className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={handleConfirmDelete}
//                   className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
//                 >
//                   Delete
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* EDIT CONFIRM */}
//       {isConfirmEditOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
//             <div className="p-6">
//               <h2 className="text-xl font-semibold text-gray-900 mb-2">Save Changes</h2>
//               <p className="text-gray-600 text-lg mb-6">
//                 Are you sure you want to save all changes to the factors?
//               </p>
//               <div className="flex justify-end space-x-3">
//                 <button
//                   onClick={handleCancelEdit}
//                   className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   onClick={handleSaveEdit}
//                   className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
//                 >
//                   Save Changes
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* CREATE FACTOR MODAL */}
//       {isPopupOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4 overflow-y-auto">
//           <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl my-8">
//             <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
//               <div>
//                 <h3 className="text-xl font-semibold text-black">Create New Factor</h3>
//                 <p className="text-gray-600 text-sm mt-0.5">
//                   Configure a pricing factor for your property
//                 </p>
//               </div>
//               <button onClick={handleNewFactorClose} className="text-gray-400 hover:text-gray-600">
//                 <FiX size={20} />
//               </button>
//             </div>

//             <div className="p-6 space-y-4 max-h-[65vh] overflow-y-auto">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-black text-sm font-semibold mb-1">
//                     Factor Name *
//                   </label>
//                   <input
//                     type="text"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none text-sm"
//                     placeholder="e.g., AC, Breakfast, Diwali Premium"
//                     value={newFactor.title}
//                     onChange={(e) => setNewFactor({ ...newFactor, title: e.target.value })}
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-black text-sm font-semibold mb-1">
//                     Factor Type *
//                   </label>
//                   <select
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none text-sm"
//                     value={newFactor.factor_type}
//                     onChange={(e) => setNewFactor({ ...newFactor, factor_type: e.target.value })}
//                   >
//                     {FACTOR_TYPES.map((t) => (
//                       <option key={t.value} value={t.value}>
//                         {t.label}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//               </div>

//               <div>
//                 <label className="block text-black text-sm font-semibold mb-1">
//                   Description (Optional)
//                 </label>
//                 <input
//                   type="text"
//                   className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none text-sm"
//                   placeholder="Short note about this factor"
//                   value={newFactor.description}
//                   onChange={(e) => setNewFactor({ ...newFactor, description: e.target.value })}
//                 />
//               </div>

//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-black text-sm font-semibold mb-1">
//                     Value Type *
//                   </label>
//                   <select
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none text-sm"
//                     value={newFactor.value_type}
//                     onChange={(e) => setNewFactor({ ...newFactor, value_type: e.target.value })}
//                   >
//                     {VALUE_TYPES.map((v) => (
//                       <option key={v.value} value={v.value}>
//                         {v.label}
//                       </option>
//                     ))}
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-black text-sm font-semibold mb-1">
//                     Default Value *{' '}
//                     <span className="text-gray-500 font-normal">
//                       ({newFactor.value_type === 'flat' ? '₹' : 'e.g. 10 for 10%'})
//                     </span>
//                   </label>
//                   <input
//                     type="number"
//                     step="0.01"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none text-sm"
//                     placeholder={newFactor.value_type === 'flat' ? '400' : '10'}
//                     value={newFactor.default_value}
//                     onChange={(e) => setNewFactor({ ...newFactor, default_value: e.target.value })}
//                   />
//                 </div>
//               </div>

//               {/* ⬅️ NEW: Min Guests — only for Auto-Calculated factors (Extra Guest) */}
//               {newFactor.factor_type === 'auto' && (
//                 <div className="border border-purple-200 bg-purple-50 rounded-md p-3">
//                   <label className="block text-black text-sm font-semibold mb-1">
//                     Included Guests (Free) *
//                   </label>
//                   <p className="text-xs text-gray-600 mb-2">
//                     Charges apply only for guests beyond this number.
//                     <br />
//                     <span className="italic">
//                       Example: Set to <b>2</b> and value ₹{newFactor.default_value || '0'} → first 2
//                       guests are free, 3rd guest onward charged ₹{newFactor.default_value || '0'}{' '}
//                       each.
//                     </span>
//                   </p>
//                   <input
//                     type="number"
//                     min="0"
//                     step="1"
//                     className="w-full px-3 py-2 border border-gray-300 rounded-md outline-none text-sm"
//                     placeholder="e.g., 2"
//                     value={newFactor.min_guests}
//                     onChange={(e) => setNewFactor({ ...newFactor, min_guests: e.target.value })}
//                   />
//                 </div>
//               )}

//               {/* Per-room-type values */}
//               <div className="border border-gray-200 rounded-md p-3">
//                 <div className="flex justify-between items-center mb-2">
//                   <div>
//                     <label className="block text-black text-sm font-semibold">
//                       Per-Room-Type Values (Optional)
//                     </label>
//                     <p className="text-xs text-gray-500">
//                       Override the default value for specific room types
//                     </p>
//                   </div>
//                   <button
//                     onClick={handleAddNewRoomTypeValue}
//                     disabled={roomTypesLoading || roomTypes.length === 0}
//                     className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
//                   >
//                     <FiPlus size={12} /> Add
//                   </button>
//                 </div>

//                 {roomTypesLoading ? (
//                   <p className="text-xs text-gray-400 italic">Loading room types...</p>
//                 ) : roomTypes.length === 0 ? (
//                   <p className="text-xs text-red-500 italic">
//                     No room types available. Please add room types first.
//                   </p>
//                 ) : newFactor.per_room_type_values.length === 0 ? (
//                   <p className="text-xs text-gray-400 italic">
//                     No per-room-type overrides. Default value will be used for all room types.
//                   </p>
//                 ) : (
//                   <div className="space-y-2">
//                     {newFactor.per_room_type_values.map((item, idx) => (
//                       <div key={idx} className="flex items-center gap-2">
//                         <select
//                           value={item.roomtypeid || ''}
//                           onChange={(e) =>
//                             handleNewRoomTypeValueChange(idx, 'roomtypeid', e.target.value)
//                           }
//                           className="flex-1 border rounded px-2 py-1 text-sm"
//                         >
//                           <option value="">-- Select Room Type --</option>
//                           {roomTypes.map((rt) => (
//                             <option key={rt.roomtypeid} value={rt.roomtypeid}>
//                               {rt.roomtypename}
//                             </option>
//                           ))}
//                         </select>
//                         <input
//                           type="number"
//                           step="0.01"
//                           value={item.value}
//                           onChange={(e) =>
//                             handleNewRoomTypeValueChange(
//                               idx,
//                               'value',
//                               parseFloat(e.target.value) || 0
//                             )
//                           }
//                           className="w-24 border rounded px-2 py-1 text-sm text-center"
//                           placeholder="Value"
//                         />
//                         <button
//                           onClick={() => handleRemoveNewRoomTypeValue(idx)}
//                           className="text-red-500 hover:text-red-700"
//                         >
//                           <FiTrash2 size={14} />
//                         </button>
//                       </div>
//                     ))}
//                   </div>
//                 )}
//               </div>

//               {/* Flags */}
//               <div className="border border-gray-200 rounded-md p-3 space-y-3">
//                 <label className="block text-black text-sm font-semibold">Behavior Flags</label>

//                 <div className="flex items-center justify-between">
//                   <div>
//                     <p className="text-sm text-gray-800 font-medium">Show on Booking Page</p>
//                     <p className="text-xs text-gray-500">Guests can select this as an add-on</p>
//                   </div>
//                   <button
//                     onClick={() => setNewFactor((p) => ({ ...p, show_in_bq: !p.show_in_bq }))}
//                     className={`w-11 h-6 rounded-full transition ${
//                       newFactor.show_in_bq ? 'bg-black' : 'bg-gray-300'
//                     }`}
//                   >
//                     <span
//                       className={`block w-4 h-4 bg-white rounded-full transition-transform mt-1 ${
//                         newFactor.show_in_bq ? 'translate-x-6' : 'translate-x-1'
//                       }`}
//                     />
//                   </button>
//                 </div>

//                 <div className="flex items-center justify-between">
//                   <div>
//                     <p className="text-sm text-gray-800 font-medium">Room Amenity</p>
//                     <p className="text-xs text-gray-500">
//                       Appears as a checkbox in Room Management
//                     </p>
//                   </div>
//                   <button
//                     onClick={() =>
//                       setNewFactor((p) => ({ ...p, is_room_amenity: !p.is_room_amenity }))
//                     }
//                     className={`w-11 h-6 rounded-full transition ${
//                       newFactor.is_room_amenity ? 'bg-black' : 'bg-gray-300'
//                     }`}
//                   >
//                     <span
//                       className={`block w-4 h-4 bg-white rounded-full transition-transform mt-1 ${
//                         newFactor.is_room_amenity ? 'translate-x-6' : 'translate-x-1'
//                       }`}
//                     />
//                   </button>
//                 </div>

//                 <div className="flex items-center justify-between">
//                   <div>
//                     <p className="text-sm text-gray-800 font-medium">Auto-Apply</p>
//                     <p className="text-xs text-gray-500">
//                       Applied automatically based on conditions
//                     </p>
//                   </div>
//                   <button
//                     onClick={() => setNewFactor((p) => ({ ...p, auto_apply: !p.auto_apply }))}
//                     className={`w-11 h-6 rounded-full transition ${
//                       newFactor.auto_apply ? 'bg-black' : 'bg-gray-300'
//                     }`}
//                   >
//                     <span
//                       className={`block w-4 h-4 bg-white rounded-full transition-transform mt-1 ${
//                         newFactor.auto_apply ? 'translate-x-6' : 'translate-x-1'
//                       }`}
//                     />
//                   </button>
//                 </div>
//               </div>

//               <div className="flex items-center justify-between pt-2">
//                 <div>
//                   <label className="block text-black text-sm font-semibold">Factor Status</label>
//                   <p className="text-gray-700 text-xs">
//                     {newFactor.isActive ? 'Active — used in calculations' : 'Inactive'}
//                   </p>
//                 </div>
//                 <button
//                   onClick={() => setNewFactor((p) => ({ ...p, isActive: !p.isActive }))}
//                   className={`w-11 h-6 rounded-full transition ${
//                     newFactor.isActive ? 'bg-black' : 'bg-gray-300'
//                   }`}
//                 >
//                   <span
//                     className={`block w-4 h-4 bg-white rounded-full transition-transform mt-1 ${
//                       newFactor.isActive ? 'translate-x-6' : 'translate-x-1'
//                     }`}
//                   />
//                 </button>
//               </div>
//             </div>

//             <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
//               <button
//                 onClick={handleNewFactorClose}
//                 className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleNewFactorSave}
//                 className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800"
//               >
//                 Create Factor
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer className="mt-12" />
//     </div>
//   );
// };

// export default ModifyFactors;
